import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Users,
  BookOpen,
  TrendingUp,
  Calendar,
  Award,
  AlertCircle,
  BarChart3,
  PieChart,
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  UserCheck,
  GraduationCap
} from 'lucide-react';
import { useRole } from '@/hooks/useRole';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart as RechartsPieChart, Cell, LineChart, Line, Pie } from 'recharts';
import { StatsCard } from '@/components/dashboard/StatsCard';
import DrillDownDialog from '@/components/admin/DrillDownDialog';
import AdminReporting from '@/components/admin/AdminReporting';
import { AIInsights } from '@/components/admin/AIInsights';

interface DashboardStats {
  totalUsers: number;
  totalCourses: number;
  totalEnrollments: number;
  completionRate: number;
  pendingProposals: number;
  activeInstructors: number;
}

interface ReportData {
  usersByRole: Array<{ name: string; value: number; color: string }>;
  usersByDepartment: Array<{ name: string; count: number }>;
  coursesByCategory: Array<{ name: string; count: number }>;
  enrollmentTrends: Array<{ date: string; enrollments: number; completions: number }>;
  topCourses: Array<{ name: string; enrollments: number }>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export default function AdminDashboard() {
  const { isAdmin, loading: roleLoading } = useRole();
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalCourses: 0,
    totalEnrollments: 0,
    completionRate: 0,
    pendingProposals: 0,
    activeInstructors: 0
  });
  const [reportData, setReportData] = useState<ReportData>({
    usersByRole: [],
    usersByDepartment: [],
    coursesByCategory: [],
    enrollmentTrends: [],
    topCourses: []
  });
  const [loading, setLoading] = useState(true);
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownData, setDrillDownData] = useState<any[]>([]);
  const [drillDownType, setDrillDownType] = useState<'users' | 'courses' | 'enrollments' | 'completions' | 'proposals' | 'instructors'>('users');
  const [drillDownTitle, setDrillDownTitle] = useState('');

  useEffect(() => {
    if (!roleLoading && isAdmin) {
      loadDashboardData();
    }
  }, [isAdmin, roleLoading]);

  const handleChartClick = async (chartType: string) => {
    try {
      let data: any[] = [];
      let title = '';

      switch (chartType) {
        case 'usersByRole':
          // Get all user roles first
          const userRolesData = await supabase
            .from('user_roles')
            .select('user_id, role')
            .eq('is_active', true);
          
          if (userRolesData.data && userRolesData.data.length > 0) {
            // Get profile data for these users - ensure valid UUIDs
            const validUserIds = userRolesData.data
              .map(ur => ur.user_id)
              .filter(id => id && typeof id === 'string' && id.length === 36); // Basic UUID length check
            
            if (validUserIds.length > 0) {
              const profilesData = await supabase
                .from('profiles')
                .select('id, first_name, last_name, email, department, position_title, phone, school_name')
                .in('id', validUserIds);
              
              // Combine the data
              data = profilesData.data?.map(profile => {
                const userRole = userRolesData.data.find(ur => ur.user_id === profile.id);
                return {
                  ...profile,
                  role: userRole?.role || 'user'
                };
              }) || [];
            }
          }
          title = 'Users by Role Details';
          setDrillDownType('users');
          break;
        
        case 'topCourses':
          const coursesData = await supabase
            .from('courses')
            .select('id, title, description, status, duration_hours, max_participants, location, created_at')
            .eq('status', 'published')
            .limit(20);
          data = coursesData.data || [];
          title = 'Top Courses Details';
          setDrillDownType('courses');
          break;
        
        case 'usersByDepartment':
          const deptData = await supabase
            .from('profiles')
            .select('id, first_name, last_name, email, department, position_title, phone, school_name')
            .limit(50);
          data = deptData.data || [];
          title = 'Users by Department Details';
          setDrillDownType('users');
          break;
        
        case 'coursesByCategory':
          const catData = await supabase
            .from('courses')
            .select(`
              id, 
              title, 
              description, 
              status,
              duration_hours,
              max_participants,
              location,
              course_categories(name)
            `)
            .eq('status', 'published')
            .limit(20);
          data = catData.data || [];
          title = 'Courses by Category Details';
          setDrillDownType('courses');
          break;
        
        case 'enrollmentTrends':
          const trendsData = await supabase
            .from('enrollments')
            .select(`
              id, 
              enrollment_date, 
              status,
              enrollment_type
            `)
            .order('enrollment_date', { ascending: false })
            .limit(50);
          data = trendsData.data || [];
          title = 'Enrollment Trends Details';
          setDrillDownType('enrollments');
          break;
      }

      if (data.length === 0) {
        toast.info('No data available for this view');
        return;
      }

      setDrillDownData(data);
      setDrillDownTitle(title);
      setDrillDownOpen(true);
    } catch (error) {
      console.error('Error loading chart drill-down:', error);
      toast.error('Failed to load chart details');
    }
  };

  const handleCardClick = async (type: 'users' | 'courses' | 'enrollments' | 'completions' | 'proposals' | 'instructors') => {
    try {
      let data: any[] = [];
      let title = '';

      switch (type) {
        case 'users':
          const usersResponse = await supabase
            .from('profiles')
            .select('id, first_name, last_name, email, department, position_title, employment_status');
          data = usersResponse.data || [];
          title = 'All Users';
          break;
        
        case 'courses':
          const coursesResponse = await supabase
            .from('courses')
            .select('id, title, description, status, duration_hours, max_participants, is_online, location');
          data = coursesResponse.data || [];
          title = 'All Courses';
          break;
        
        case 'enrollments':
          const enrollmentsResponse = await supabase
            .from('enrollments')
            .select('id, enrollment_date, status, course_id, participant_id');
          data = enrollmentsResponse.data || [];
          title = 'All Enrollments';
          break;
        
        case 'completions':
          const completionsResponse = await supabase
            .from('enrollments')
            .select('id, enrollment_date, completion_date, status, course_id, participant_id')
            .eq('status', 'completed');
          data = completionsResponse.data || [];
          title = 'Completed Courses';
          break;
        
        case 'proposals':
          const proposalsResponse = await supabase
            .from('course_proposals')
            .select('id, title, status, created_at, proposer_name, duration_hours')
            .eq('status', 'pending');
          data = proposalsResponse.data || [];
          title = 'Pending Proposals';
          break;
        
        case 'instructors':
          // Get instructor roles first
          const instructorRolesData = await supabase
            .from('user_roles')
            .select('user_id')
            .eq('role', 'instructor')
            .eq('is_active', true);
          
          if (instructorRolesData.data) {
            // Get profile data for these instructors
            const instructorIds = instructorRolesData.data.map(ir => ir.user_id);
            const instructorProfilesData = await supabase
              .from('profiles')
              .select('id, first_name, last_name, email, department, position_title')
              .in('id', instructorIds);
            
            data = instructorProfilesData.data || [];
          }
          title = 'Active Instructors';
          break;
      }

      setDrillDownData(data);
      setDrillDownType(type);
      setDrillDownTitle(title);
      setDrillDownOpen(true);
    } catch (error) {
      console.error('Error loading drill-down data:', error);
      toast.error('Failed to load detailed data');
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load basic stats
      const [
        usersResponse,
        coursesResponse,
        enrollmentsResponse,
        proposalsResponse,
        rolesResponse
      ] = await Promise.all([
        supabase.from('profiles').select('*'),
        supabase.from('courses').select('*'),
        supabase.from('enrollments').select('*'),
        supabase.from('course_proposals').select('*').eq('status', 'pending'),
        supabase.from('user_roles').select('*')
      ]);

      if (usersResponse.error) throw usersResponse.error;
      if (coursesResponse.error) throw coursesResponse.error;
      if (enrollmentsResponse.error) throw enrollmentsResponse.error;
      if (proposalsResponse.error) throw proposalsResponse.error;
      if (rolesResponse.error) throw rolesResponse.error;

      const users = usersResponse.data || [];
      const courses = coursesResponse.data || [];
      const enrollments = enrollmentsResponse.data || [];
      const proposals = proposalsResponse.data || [];
      const roles = rolesResponse.data || [];

      // Calculate stats
      const completedEnrollments = enrollments.filter(e => e.status === 'completed');
      const completionRate = enrollments.length > 0 ? (completedEnrollments.length / enrollments.length) * 100 : 0;
      const activeInstructors = roles.filter(r => r.role === 'instructor').length;

      setStats({
        totalUsers: users.length,
        totalCourses: courses.length,
        totalEnrollments: enrollments.length,
        completionRate,
        pendingProposals: proposals.length,
        activeInstructors
      });

      // Generate report data
      await loadReportData(users, courses, enrollments, roles);

    } catch (error) {
      console.error('Error loading admin dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadReportData = async (users: any[], courses: any[], enrollments: any[], roles: any[]) => {
    try {
      // Users by role
      const roleCounts = roles.reduce((acc, role) => {
        acc[role.role] = (acc[role.role] || 0) + 1;
        return acc;
      }, {});

      const usersByRole = Object.entries(roleCounts).map(([role, count], index) => ({
        name: role.charAt(0).toUpperCase() + role.slice(1),
        value: count as number,
        color: COLORS[index % COLORS.length]
      }));

      // Users by department
      const deptCounts = users.reduce((acc, user) => {
        const dept = user.department || 'Unknown';
        acc[dept] = (acc[dept] || 0) + 1;
        return acc;
      }, {});

      const usersByDepartment = Object.entries(deptCounts).map(([name, count]) => ({
        name,
        count: count as number
      }));

      // Courses by category
      const categoriesResponse = await supabase
        .from('courses')
        .select('*, course_categories(name)')
        .eq('status', 'published');

      if (!categoriesResponse.error) {
        const catCounts = categoriesResponse.data.reduce((acc, course) => {
          const category = course.course_categories?.name || 'Unknown';
          acc[category] = (acc[category] || 0) + 1;
          return acc;
        }, {});

        const coursesByCategory = Object.entries(catCounts).map(([name, count]) => ({
          name,
          count: count as number
        }));

        // Top courses by enrollment
        const courseCounts = enrollments.reduce((acc, enrollment) => {
          acc[enrollment.course_id] = (acc[enrollment.course_id] || 0) + 1;
          return acc;
        }, {});

        const topCoursesData = await Promise.all(
          Object.entries(courseCounts)
            .sort(([,a], [,b]) => (b as number) - (a as number))
            .slice(0, 5)
            .map(async ([courseId, count]) => {
              const courseResponse = await supabase
                .from('courses')
                .select('title')
                .eq('id', courseId)
                .single();
              
              return {
                name: courseResponse.data?.title || 'Unknown Course',
                enrollments: count as number
              };
            })
        );

        // Enrollment trends (mock data for demo)
        const enrollmentTrends = [
          { date: '2024-01', enrollments: 45, completions: 32 },
          { date: '2024-02', enrollments: 52, completions: 38 },
          { date: '2024-03', enrollments: 48, completions: 41 },
          { date: '2024-04', enrollments: 61, completions: 45 },
          { date: '2024-05', enrollments: 55, completions: 48 },
          { date: '2024-06', enrollments: 67, completions: 52 }
        ];

        setReportData({
          usersByRole,
          usersByDepartment,
          coursesByCategory,
          enrollmentTrends,
          topCourses: topCoursesData
        });
      }
    } catch (error) {
      console.error('Error loading report data:', error);
    }
  };

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <AlertCircle className="h-16 w-16 text-destructive" />
              <h2 className="text-2xl font-bold">Access Denied</h2>
              <p className="text-muted-foreground">
                You need administrator privileges to access this page.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const chartConfig = {
    enrollments: { label: "Enrollments", color: "#0088FE" },
    completions: { label: "Completions", color: "#00C49F" },
  };

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-foreground">Admin Dashboard</h1>
          <p className="text-muted-foreground">System overview and analytics</p>
        </div>
        <Button onClick={loadDashboardData} disabled={loading}>
          <Activity className="w-4 h-4 mr-2" />
          Refresh Data
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        <div onClick={() => handleCardClick('users')}>
          <StatsCard
            title="Total Users"
            value={stats.totalUsers}
            description="Active users"
            icon={Users}
            variant="primary"
            className="cursor-pointer hover:shadow-lg transition-shadow"
          />
        </div>
        <div onClick={() => handleCardClick('courses')}>
          <StatsCard
            title="Total Courses"
            value={stats.totalCourses}
            description="All courses"
            icon={BookOpen}
            className="cursor-pointer hover:shadow-lg transition-shadow"
          />
        </div>
        <div onClick={() => handleCardClick('enrollments')}>
          <StatsCard
            title="Total Enrollments"
            value={stats.totalEnrollments}
            description="All time"
            icon={GraduationCap}
            className="cursor-pointer hover:shadow-lg transition-shadow"
          />
        </div>
        <div onClick={() => handleCardClick('completions')}>
          <StatsCard
            title="Completion Rate"
            value={`${Math.round(stats.completionRate)}%`}
            description="Course completion"
            icon={CheckCircle}
            variant={stats.completionRate > 80 ? "primary" : "default"}
            className="cursor-pointer hover:shadow-lg transition-shadow"
          />
        </div>
        <div onClick={() => handleCardClick('proposals')}>
          <StatsCard
            title="Pending Proposals"
            value={stats.pendingProposals}
            description="Need review"
            icon={Clock}
            variant={stats.pendingProposals > 0 ? "default" : "primary"}
            className="cursor-pointer hover:shadow-lg transition-shadow"
          />
        </div>
        <div onClick={() => handleCardClick('instructors')}>
          <StatsCard
            title="Active Instructors"
            value={stats.activeInstructors}
            description="Teaching staff"
            icon={UserCheck}
            className="cursor-pointer hover:shadow-lg transition-shadow"
          />
        </div>
      </div>

      {/* Reports Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="users">User Analytics</TabsTrigger>
          <TabsTrigger value="courses">Course Analytics</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="insights">AI Insights</TabsTrigger>
          <TabsTrigger value="reporting">Custom Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleChartClick('usersByRole')}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Users by Role
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie data={reportData.usersByRole} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
                        {reportData.usersByRole.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <ChartTooltip />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleChartClick('topCourses')}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Top Courses
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.topCourses}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Bar dataKey="enrollments" fill="#0088FE" />
                      <ChartTooltip />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleChartClick('usersByDepartment')}>
            <CardHeader>
              <CardTitle>Users by Department</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reportData.usersByDepartment}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Bar dataKey="count" fill="#00C49F" />
                    <ChartTooltip />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="courses" className="space-y-6">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleChartClick('coursesByCategory')}>
            <CardHeader>
              <CardTitle>Courses by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reportData.coursesByCategory}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Bar dataKey="count" fill="#FFBB28" />
                    <ChartTooltip />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Enrollment vs Completion Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={reportData.enrollmentTrends}>
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Line type="monotone" dataKey="enrollments" stroke="#0088FE" strokeWidth={2} />
                    <Line type="monotone" dataKey="completions" stroke="#00C49F" strokeWidth={2} />
                    <ChartTooltip />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insights" className="space-y-6">
          <AIInsights stats={stats} reportData={reportData} />
        </TabsContent>

        <TabsContent value="reporting" className="space-y-6">
          <AdminReporting />
        </TabsContent>
      </Tabs>

      {/* Drill Down Dialog */}
      <DrillDownDialog
        isOpen={drillDownOpen}
        onClose={() => setDrillDownOpen(false)}
        type={drillDownType}
        data={drillDownData}
        title={drillDownTitle}
      />
    </div>
  );
}